import { useEffect, useMemo, useState } from "react";
import { useT, useSeo, fmtDateTime, userTz } from "@/lib/i18n";
import { pub } from "@/lib/api";
import { PageHead, Notice } from "@/components/bits";
import { Show } from "@clerk/react";
import { useApi } from "@/lib/api";
import { toast } from "sonner";

const KEYS = ["new_moon", "waxing_crescent", "first_quarter", "waxing_gibbous", "full_moon", "waning_gibbous", "last_quarter", "waning_crescent"];

export default function Calendar() {
  const { t, tl, pick, locale } = useT();
  useSeo(null, tl("nav")[7]);
  const api = useApi();
  const [tz, setTz] = useState(userTz());
  const [date, setDate] = useState(() => new Intl.DateTimeFormat("en-CA", { timeZone: userTz() }).format(new Date()));
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const L = tl("calLabels"), P = tl("phases");
  const tzs = useMemo(() => { let l = [tz]; try { l = Intl.supportedValuesOf("timeZone"); } catch {} return l.includes(tz) ? l : [tz, ...l]; }, [tz]);
  useEffect(() => { setFailed(false); pub.get("/public/calendar", { params: { date, timezone: tz } }).then((r) => setD(r.data)).catch(() => setFailed(true)); }, [date, tz]);
  const a = d?.astronomy;
  const stale = a && Date.now() - new Date(a.calculated_at).getTime() > 24 * 3600e3;
  return (
    <>
      <PageHead title={t("calH1")} lead={t("calIntro")} dark />
      <section className="wrap py-12 grid md:grid-cols-[1.2fr_1fr] gap-10">
        <div className="card p-5 sm:p-8 grid gap-4 min-w-0" data-testid="astro-card">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="field">{L[0]}<input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cal-date" /></label>
            <label className="field">{tl("fields")[2]}<select value={tz} onChange={(e) => setTz(e.target.value)} data-testid="cal-tz">{tzs.map((z) => <option key={z}>{z}</option>)}</select></label>
          </div>
          {(failed || (d && !d.astronomy_available) || stale) ? <Notice testid="astro-unavailable">{t("calUnavailable")}</Notice> : a && (
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              <div><dt className="eyebrow">{L[1]}</dt><dd className="serif text-3xl" data-testid="astro-phase">{P[KEYS.indexOf(a.phase)]}</dd></div>
              <div><dt className="eyebrow">{L[2]}</dt><dd className="serif text-3xl" data-testid="astro-illum">{a.illumination_pct}%</dd></div>
              <div><dt className="eyebrow">{L[3]}</dt><dd>{fmtDateTime(a.next_new_moon, tz, locale)}</dd></div>
              <div><dt className="eyebrow">{L[4]}</dt><dd>{fmtDateTime(a.next_full_moon, tz, locale)}</dd></div>
              <div className="sm:col-span-2"><dt className="eyebrow">{L[5]}</dt><dd data-testid="astro-mercury">{a.mercury.is_retrograde ? `${L[9]} ${a.mercury.current.end}` : `${L[10]}: ${a.mercury.next?.start} → ${a.mercury.next?.end}`}</dd></div>
              <div className="sm:col-span-2 text-xs text-[var(--muted)]"><dt className="inline">{L[6]}: </dt><dd className="inline">{a.source}</dd> · {L[7]}: {fmtDateTime(a.calculated_at, tz, locale, { dateStyle: "short", timeStyle: "short" })}<p className="mt-1">{t("hemisphere")}</p></div>
            </dl>)}
        </div>
        <div className="grid gap-6 content-start">
          <div className="card p-8"><p className="eyebrow">{L[8]}</p><p className="serif text-3xl mt-2" data-testid="daily-text">{d ? pick(d.daily.text) : "…"}</p><p className="help mt-4">{t("dailyLabel")}</p>
            <Show when="signed-in"><button className="btn btn-ghost btn-sm mt-4" data-testid="save-daily" onClick={() => api.post("/reflections/save", { type: "daily", ref_id: `D${d?.daily.day_of_month}`, locale }).then(() => toast.success(tl("controls")[0]))}>{t("calCta")}</button></Show></div>
          <div className="card p-8"><p className="eyebrow">{L[11]}</p>{d?.events?.length ? d.events.map((e) => <p key={e.id}>{pick(e.title_i18n)} · {fmtDateTime(e.start_utc, tz, locale)}</p>) : <p data-testid="live-empty">{t("liveEmpty")}</p>}</div>
        </div>
      </section>
    </>
  );
}
